import {
  defaultPageSizeOptions, GridColumnRenderers,
  GridViewMetadata,
} from '../../../grid/gridView/types';
import { ColumnDetails, ColumnType, GridEvents } from '../../../grid/types/common';
import { CloudStagingListWebComponentSchema, Column, ColumnKeys } from '../types';
import { CloudStagingListNirvanaDataProvider } from '../dataProvider/CloudStagingListNirvanaDataProvider';
import { Dependencies } from '../types/dependencies';
import { html } from 'lit';
import { SelectOption } from '../../../ui/gzSelect/types';
import { dateFormatter } from "@nirvana/shared/time";
import { stagingProfilesRenderer, versionRenderer } from './customGridRenderers';
import { GridViewWebComponent } from '../../../grid/gridView';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { htmlDecode } from '../../../utils/escape.utils';

export class GridConfig {

  public getDataForView(grid: GridViewWebComponent<Column, ColumnKeys>, dependencies: Dependencies): CloudStagingListWebComponentSchema {
    const { translateFn } = dependencies;

    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'web-access-control-scheduler',
      filters: [
        {
          [ColumnKeys.VERSION]: { contains: '' },
        },
      ],
      predefinedFiltersKeys: [ColumnKeys.VERSION],
      columns: this.getColumns(),
      pageSize: 100,
      isCompactViewChecked: false,
      hasSelectableRows: false,
      hasPagination: true,
      pageSizeOptions: defaultPageSizeOptions,
      columnRenderers: this.getColumnRenderers(grid, dependencies),
      columnConfigs: [],
      allColumnDetails: this.getColumnDetails(translateFn),
      pageNumber: 1,
      dataProvider: new CloudStagingListNirvanaDataProvider(dependencies),
      enableCSVExport: false,
      removeFilters: true,
      hideHeaderActions: true,
      showGlobalActions: true,
      inlineRowActions: this.getInlineRowActions(translateFn),
    };

    return {
      viewMetaData,
    };
  }

  private getColumnRenderers(grid: GridViewWebComponent<Column, ColumnKeys>, dependencies: Dependencies): GridColumnRenderers {
    const { translateFn } = dependencies;
    return {
      [ColumnKeys.VERSION]: versionRenderer.bind(this, dependencies),
      [ColumnKeys.STAGING_PROFILE]: stagingProfilesRenderer.bind(this, grid, dependencies),
      [ColumnKeys.VENDOR_RINGS]: rowData => {
        const vendorRings = rowData?.item?.vendorRings;
        const vendorRingsString = vendorRings ? vendorRings.join(', ') : '';
        return html`
          <gz-grid-cell-default-renderer
            .label="${vendorRingsString}"
          ></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.VERSION_PUBLISHED]: rowData => {
        return html`
          <gz-grid-cell-default-renderer
            .label="${dateFormatter(rowData?.item?.publishInfo?.startDate, '')}"
          ></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.HAS_KIT]: rowData => {
        return html`
          <gz-grid-cell-default-renderer
            .label="${rowData?.item?.hasKit === true ? translateFn('Yes') : translateFn('No')}"
          ></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.LAST_MODIFIED]: rowData => {

        if (isEmpty(rowData?.item?.lastModified)) {
          return html`
          <gz-grid-cell-default-renderer
            .label="${translateFn('N/A')}"
          ></gz-grid-cell-default-renderer>`;
        }
        const formattedDate = dateFormatter(rowData?.item?.lastModified, '');

        return html`
          <gz-grid-cell-default-renderer
            .label="${htmlDecode(formattedDate)}"
          ></gz-grid-cell-default-renderer>`;
      },
    };
  }

  private getColumns(): Column[] {
    return [
      Column.VERSION,
      Column.STAGING_PROFILE,
      Column.VENDOR_RINGS,
      Column.VERSION_PUBLISHED,
      Column.LAST_MODIFIED,
      Column.HAS_KIT,
    ];
  }

  private getColumnDetails(
    translateFn: Function
  ): Array<ColumnDetails<Column, ColumnKeys>> {
    return [
      {
        column: Column.VERSION,
        key: ColumnKeys.VERSION,
        columnName: translateFn('Version'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filteringDisabled: true,

      },
      {
        column: Column.VERSION_PUBLISHED,
        key: ColumnKeys.VERSION_PUBLISHED,
        columnName: translateFn('Released'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filteringDisabled: true,
      },
      {
        column: Column.STAGING_PROFILE,
        key: ColumnKeys.STAGING_PROFILE,
        columnName: translateFn('Staging rings'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },

      {
        column: Column.HAS_KIT,
        key: ColumnKeys.HAS_KIT,
        columnName: translateFn('Installation kit'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
        filteringDisabled: true,
      },
      {
        column: Column.LAST_MODIFIED,
        key: ColumnKeys.LAST_MODIFIED,
        columnName: translateFn('Last modified'),
        type: ColumnType.DATE,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      }
    ];
  }

  private getInlineRowActions(translateFn: Function): Array<SelectOption> {
    return [
      {
        title: translateFn('Edit'),
        value: GridEvents.EDIT_ROW,
      },
    ];
  }
}
